import {
  BrowserAuthError,
  NavigationClient,
  PublicClientApplication,
  type Configuration,
  type IPublicClientApplication,
  type NavigationOptions,
  type RedirectRequest,
} from "@azure/msal-browser";
import { Browser } from "@capacitor/browser";
import { registerPlugin } from "@capacitor/core";
import { NATIVE_AUTH_REDIRECT_URI, nativeSpaRedirectUri } from "./redirect";
import { isNativeShell } from "./platform";

type AuthBrowserPlugin = {
  open: (options: {
    url: string;
    redirectHost?: string;
    redirectPath?: string;
    redirectScheme?: string;
  }) => Promise<void>;
  close: () => Promise<void>;
  consumePending: () => Promise<{ url?: string; canceled?: boolean }>;
  addListener: (
    eventName: "redirect" | "canceled",
    listener: (event: { url?: string; resultCode?: number }) => void,
  ) => Promise<{ remove: () => Promise<void> }>;
};

export const AuthBrowser = registerPlugin<AuthBrowserPlugin>("AuthBrowser");

export const DEFAULT_ENTRA_TENANT_ID = "a070396c-a0f2-4283-a321-897f69dd34ce";
export const DEFAULT_ENTRA_CLIENT_ID = "388a1763-defd-44d4-8f87-7b78b1820121";

export function entraClientId(): string {
  return (import.meta.env.VITE_ENTRA_CLIENT_ID ?? DEFAULT_ENTRA_CLIENT_ID).trim();
}

export function entraTenantId(): string {
  return (import.meta.env.VITE_ENTRA_TENANT_ID ?? DEFAULT_ENTRA_TENANT_ID).trim();
}

export const COPILOT_STUDIO_SCOPE = "https://api.powerplatform.com/CopilotStudio.Copilots.Invoke";

export const loginRequest: RedirectRequest = {
  scopes: ["openid", "profile", "email", "User.Read"],
  extraScopesToConsent: [COPILOT_STUDIO_SCOPE],
};

export function authRedirectUri(origin?: string, native = isNativeShell()): string {
  if (native) {
    return nativeSpaRedirectUri(import.meta.env.VITE_API_BASE_URL) ?? NATIVE_AUTH_REDIRECT_URI;
  }
  return origin ?? (typeof window !== "undefined" ? window.location.origin : "http://localhost:5173");
}

export async function openSystemBrowser(url: string): Promise<void> {
  const authorize = new URL(url);
  if (authorize.hostname.endsWith("microsoftonline.com") && authorize.pathname.includes("/authorize")) {
    authorize.searchParams.set("response_mode", "query");
  }
    const href = authorize.toString();
    if (isNativeShell()) {
      try {
        const spa = nativeSpaRedirectUri(import.meta.env.VITE_API_BASE_URL);
        const spaUrl = spa ? new URL(spa) : undefined;
        await AuthBrowser.open({
        url: href,
        redirectScheme: NATIVE_AUTH_REDIRECT_URI.replace(/:\/\/auth$/, ""),
        ...(spaUrl
          ? { redirectHost: spaUrl.hostname, redirectPath: spaUrl.pathname || "/auth-callback" }
          : {}),
      });
      return;
    } catch (error) {
      console.error(error);
    }
  }
  await Browser.open({ url: href });
}

class NativeNavigationClient extends NavigationClient {
  async navigateExternal(url: string, options: NavigationOptions): Promise<boolean> {
    if (isNativeShell()) {
      await openSystemBrowser(url);
      return false;
    }
    return super.navigateExternal(url, options);
  }
}

function interceptRedirect(url: string): boolean | void {
  if (!isNativeShell()) {
    return;
  }
  void openSystemBrowser(url).catch((error: unknown) => {
    console.error(error);
  });
  return false;
}

export function msalConfig(): Configuration {
  const clientId = entraClientId();
  const tenantId = entraTenantId();
  const native = isNativeShell();
  const origin = typeof window !== "undefined" ? window.location.origin : "http://localhost:5173";
  return {
    auth: {
      clientId,
      authority: `https://login.microsoftonline.com/${tenantId}`,
      redirectUri: authRedirectUri(origin, native),
      postLogoutRedirectUri: authRedirectUri(origin, native),
      ...(native ? { onRedirectNavigate: (url: string) => interceptRedirect(url) } : {}),
    },
    cache: {
      cacheLocation: native ? "localStorage" : "sessionStorage",
    },
    system: {
      allowPlatformBroker: false,
      ...(native
        ? {
            navigationClient: new NativeNavigationClient(),
            redirectNavigationTimeout: 300_000,
          }
        : {}),
    },
  };
}

let instance: PublicClientApplication | undefined;

export function resetMsalInstance(): void {
  instance = undefined;
}

export function getMsalInstance(): PublicClientApplication {
  if (!instance) {
    instance = new PublicClientApplication(msalConfig());
  }
  return instance;
}

export function clearMsalInteractionLock(): void {
  if (typeof window === "undefined") {
    return;
  }
  for (const store of [window.sessionStorage, window.localStorage]) {
    const keys: string[] = [];
    for (let i = 0; i < store.length; i++) {
      const key = store.key(i);
      if (key && /interaction/i.test(key)) {
        keys.push(key);
      }
    }
    for (const key of keys) {
      store.removeItem(key);
    }
  }
}

export async function waitForNativeBridge(): Promise<void> {
  for (let attempt = 0; attempt < 40; attempt++) {
    if (isNativeShell()) {
      return;
    }
    await new Promise((resolve) => {
      setTimeout(resolve, 50);
    });
  }
}

/** Always redirect on web (no popup). Native uses Chrome Custom Tabs. */
export function preferRedirectLogin(): boolean {
  return true;
}

export async function startLogin(pca: IPublicClientApplication): Promise<void> {
  await pca.loginRedirect({
    ...loginRequest,
    ...(isNativeShell() ? { prompt: "login" } : {}),
  });
}

export async function startLogout(pca: IPublicClientApplication): Promise<void> {
  if (isNativeShell()) {
    await pca.logoutRedirect({
      postLogoutRedirectUri: authRedirectUri(),
    });
    return;
  }
  await pca.logoutRedirect();
}

export function clearAuthRedirectParams(): void {
  if (typeof window === "undefined") {
    return;
  }
  const url = new URL(window.location.href);
  for (const key of ["code", "state", "session_state", "error", "error_description", "client_info", "iss"]) {
    url.searchParams.delete(key);
  }
  if (/code=|error=|client_info=/.test(url.hash)) {
    url.hash = "";
  }
  window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
}

/** Always run on boot. Stale codes must not crash the app. */
export async function consumeRedirectResult(pca: PublicClientApplication, hash?: string) {
  try {
    return await pca.handleRedirectPromise(
      hash ? { hash, navigateToLoginRequestUrl: false } : undefined,
    );
  } catch (error) {
    if (error instanceof BrowserAuthError && error.errorCode === "no_token_request_cache_error") {
      clearAuthRedirectParams();
      return null;
    }
    throw error;
  }
}
